import { Trans, useTranslation } from 'react-i18next';

import { DesktopEntrySkyline } from '@/components/auth/desktop-entry-skyline';
import { brandCanvasClass, entryCanvasTokenOverrides } from '@/components/brand/entry-styles';
import { PulseBlocks } from '@/components/brand/pulse-blocks';
import { Gmail } from '@/components/icons/gmail';
import { Meta } from '@/components/icons/meta';
import { Microsoft } from '@/components/icons/microsoft';
import { Outlook } from '@/components/icons/outlook';

import type { ReactNode } from 'react';

interface DesktopLoginLayoutProps {
  readonly children: ReactNode;
}

/**
 * Square marks size on both axes; Meta ships as a wide wordmark, so it sizes on
 * height alone or `object-contain` shrinks it to fit a square width.
 */
const SQUARE_MARK = 'h-4.5 w-4.5';

const CHANNELS = [
  { key: 'outlook', Mark: Outlook, className: SQUARE_MARK },
  { key: 'gmail', Mark: Gmail, className: SQUARE_MARK },
  { key: 'microsoft', Mark: Microsoft, className: SQUARE_MARK },
  { key: 'meta', Mark: Meta, className: 'h-5.5 w-auto max-w-22' },
] as const;

/**
 * The desktop entry screen: the same paper the phone opens on, laid out for a
 * window.
 *
 * Side by side rather than centred. A centred column is the phone screen with
 * air poured around it, and the window's width then does nothing; here the brand
 * block and artwork hold the left and the sign-in holds the right. Below 900px
 * it folds to one column with the brand and artwork on top.
 *
 * The brand block leads the LEFT column - wordmark, then platform label, then
 * the tiles. It belongs with the artwork it is drawn in the same hand as, and
 * keeping it out of the right column leaves that column one thing only: the ways
 * in. Nothing there but methods.
 *
 * The canvas is `brandCanvasClass` - flat `#F5F3EE` in BOTH themes, continuous
 * with the shell window's own `backgroundColor`, so the cold launch never
 * flashes a different surface. Anything rendered onto it must dress itself from
 * the entry tokens: a semantic `--color-fg-*` flips with the system theme and
 * would print near-white on paper. This layout wraps FIVE routes rather than
 * `/login` alone, and the other four still dress from the shared web auth
 * chrome, so the root also carries `entryCanvasTokenOverrides` - which pins
 * those flipping tokens for the whole subtree. See that constant for why.
 *
 * The layout owns NO heading. All five routes render their own `<h1>`
 * (`/login`'s is visually hidden behind the brand block), so the wordmark here
 * is an image with a real `alt` rather than a second page-level heading
 * competing with theirs.
 *
 * ONLY the tiles animate. The sign-in column has to be readable and clickable
 * the instant the window paints, so it carries no entrance transition - pinned
 * by `e2e/desktop-entry.spec.ts`.
 */
export function DesktopLoginLayout({ children }: DesktopLoginLayoutProps) {
  const { t } = useTranslation('auth');

  return (
    <div
      className={`relative isolate flex min-h-dvh flex-col overflow-y-auto font-sohne antialiased ${brandCanvasClass}`}
      data-testid="desktop-login-layout"
      style={entryCanvasTokenOverrides}
    >
      <DesktopEntrySkyline />

      {/* A wash of the canvas colour between the skyline and the copy. Cream on
          cream: it introduces no new colour, it only knocks the linework back
          where words have to be read, and it must stay undetectable. Anchored
          to the viewport like the skyline it dims - a document-anchored wash
          would drift off it as soon as the column outgrew the window. */}
      <span
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(58%_52%_at_72%_48%,var(--color-entry-canvas)_0%,var(--color-entry-canvas)_46%,transparent_100%)] max-[900px]:bg-[radial-gradient(78%_44%_at_50%_58%,var(--color-entry-canvas)_0%,var(--color-entry-canvas)_54%,transparent_100%)]"
        data-testid="desktop-entry-scrim"
      />

      <div className="relative z-10 flex flex-1 items-center justify-center px-6 pt-8 pb-4">
        <div className="grid w-full max-w-232 items-center justify-center gap-9 max-[900px]:justify-items-center min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] min-[900px]:gap-16">
          <div className="flex min-w-0 flex-col items-center gap-7 max-[900px]:order-first">
            <div className="flex flex-col items-center gap-1.5">
              {/* The wordmark is ARTWORK - eleven drawn paths, not type. Söhne
                  at 600 is a lookalike of it, never it. The `alt` carries the
                  brand name so the image is named without minting a heading:
                  every route under this layout brings its own `<h1>`. */}
              <img
                alt="Public Pulse"
                className="block h-auto w-[min(12.5rem,24vw)] max-[900px]:w-44"
                src="/svg/logo-wordmark.svg"
              />
              {/* NO `uppercase`: the label reads `macOS Desktop`, and
                  `text-transform` would render it `MACOS DESKTOP`. */}
              <p
                className="text-xs font-medium tracking-widest text-entry-ink/32"
                data-testid="desktop-entry-platform-label"
              >
                {t('desktopLogin.productLabel')}
              </p>
            </div>

            <PulseBlocks className="w-[min(18rem,34vw)] max-[900px]:w-[min(15rem,52vw)]" />

            {/* The channel marks say what the product CONNECTS to, which is a
                different claim from how you get in. Keeping them out of the
                sign-in column is what stops Microsoft appearing twice on one
                screen meaning two different things. */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs font-medium tracking-wider text-entry-ink/30 uppercase">
                {t('mobileLogin.workWith')}
              </p>
              <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-5 p-0">
                {CHANNELS.map(({ key, Mark, className }) => (
                  <li key={key} className="flex opacity-60">
                    <Mark className={className} />
                  </li>
                ))}
              </ul>
              <p className="text-center text-xs text-entry-ink/35">
                {t('mobileLogin.channelsCaption')}
              </p>
            </div>
          </div>

          <main
            className="flex w-full max-w-88 min-w-0 flex-col"
            data-testid="desktop-entry-sign-in"
          >
            {children}
          </main>
        </div>
      </div>

      {/* Legal sits in the bottom-left corner of the SCREEN, not in the sign-in
          column: it is a statement about the product, not a step in signing in,
          and inside the column it read as the last thing you do before the
          button. The last flex row of a `min-h-dvh` column, so a short page
          pins it to the viewport floor and a tall one lets it fall below the
          content instead of covering it - which `fixed` would do at 1024x768. */}
      <footer
        className="relative z-10 px-8 pb-6 text-xs text-entry-ink/40 max-[900px]:px-6 max-[900px]:text-center"
        data-testid="desktop-entry-legal"
      >
        <Trans
          components={{
            privacy: (
              <a
                className="underline underline-offset-2 hover:text-entry-ink"
                href="https://www.publicpulse.com/privacy"
                rel="noopener noreferrer"
                target="_blank"
              >
                <span />
              </a>
            ),
            terms: (
              <a
                className="underline underline-offset-2 hover:text-entry-ink"
                href="https://www.publicpulse.com/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                <span />
              </a>
            ),
          }}
          i18nKey="footer.copyright"
          t={t}
          values={{ year: String(new Date().getFullYear()) }}
        />
      </footer>
    </div>
  );
}
